import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const VIDEO_SRC = "/videos/why-tint-your-windows.mp4";
const POSTER = "/images/why-tint-poster.jpg";

/**
 * "Why tint your windows" video block — website residential tint page only.
 * Portrait video card that opens a modal player on click.
 */
export default function WhyTintVideo() {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    // Explicit play() — mounted after a user tap, so transient activation allows sound.
    // If the browser still blocks it, native controls are there as the fallback.
    videoRef.current?.play().catch(() => {});
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close]);

  const openPlayer = () => {
    trackEvent("video_play", { video: "why-tint-your-windows", location: "website-residential-tint" });
    setOpen(true);
  };

  return (
    <section data-testid="why-tint-video-section" className="py-16 md:py-24 border-t border-neutral-200/70">
      <div className="wrap grid md:grid-cols-12 gap-10 md:gap-14 items-center">
        <div className="md:col-span-7 order-2 md:order-1">
          <div className="eyebrow eyebrow-red">Why tint your windows?</div>
          <h2 className="h-display mt-4" style={{ fontSize: "clamp(32px, 3.8vw, 52px)" }}>
            Watch what the Arizona sun is <span className="italic font-normal">really</span> doing to your home.
          </h2>
          <p className="mt-5 text-lg text-neutral-600 max-w-xl leading-relaxed">
            In just over a minute, Dave shows you exactly what ceramic window film does for your comfort, your cooling bill, and everything the sun is slowly fading. Hit play — it&apos;s worth it.
          </p>
          <button
            type="button"
            onClick={openPlayer}
            data-testid="why-tint-video-open"
            data-track="video_play"
            data-track-location="website-residential-tint"
            className="mt-8 btn-red text-base"
          >
            <Play size={16} /> Watch the 1-minute demo
          </button>
        </div>

        <div className="md:col-span-5 order-1 md:order-2">
          <button
            type="button"
            onClick={openPlayer}
            data-testid="why-tint-video-card"
            aria-label="Play video: why you should tint your windows"
            className="group relative block mx-auto w-full max-w-[320px] aspect-[9/16] rounded-3xl overflow-hidden border border-neutral-200 shadow-xl text-left"
          >
            <img src={POSTER} alt="Dave demonstrating residential window tint on a Prescott Valley home" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10" />
            <div className="absolute inset-0 grid place-items-center">
              <span className="w-16 h-16 rounded-full bg-[var(--red)] text-white grid place-items-center shadow-2xl transition-transform duration-300 group-hover:scale-110">
                <Play size={24} className="ml-1 fill-white" />
              </span>
            </div>
            <div className="absolute inset-x-0 bottom-0 p-5">
              <div className="eyebrow text-white/80">Why you should tint your windows</div>
              <div className="mt-1 text-white text-sm font-semibold">Tap to watch · 1:12</div>
            </div>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="why-tint-video-modal"
            className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm grid place-items-center p-4"
            onClick={close}
          >
            <button
              type="button"
              onClick={close}
              data-testid="why-tint-video-close"
              aria-label="Close video"
              className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors"
            >
              <X size={20} />
            </button>
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[85vh] aspect-[9/16] rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                ref={videoRef}
                data-testid="why-tint-video-player"
                src={VIDEO_SRC}
                poster={POSTER}
                controls
                playsInline
                className="w-full h-full object-cover bg-black"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
